import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { api, formatApiError } from "@/lib/api";

export default function Settings() {
  const [modalities, setModalities] = useState([]);
  const [settings, setSettings] = useState(null);
  const [paymentMethods, setPaymentMethods] = useState("");
  const [minAge, setMinAge] = useState(7);
  const [genWeeks, setGenWeeks] = useState(8);
  const [leadOrigins, setLeadOrigins] = useState("");
  const [dueDay, setDueDay] = useState(10);
  const [prorate, setProrate] = useState("half");
  const [siteUrl, setSiteUrl] = useState("");
  const [saving, setSaving] = useState("");

  const load = async () => {
    const [m, s] = await Promise.all([api.get("/modalities"), api.get("/settings")]);
    setModalities(m.data);
    setSettings(s.data);
    setPaymentMethods((s.data.payment_methods || []).join("\n"));
    setMinAge(s.data.min_age_default);
    setGenWeeks(s.data.lesson_generation_weeks ?? 8);
    setLeadOrigins((s.data.lead_origins || []).join("\n"));
    setDueDay(s.data.payment_due_day ?? 10);
    setProrate(s.data.prorate_rule ?? "half");
    setSiteUrl(s.data.site_url ?? "");
  };

  useEffect(() => {
    load().catch((e) => toast.error(formatApiError(e)));
  }, []);

  const saveModality = async (m) => {
    setSaving(m.id);
    try {
      await api.put(`/modalities/${m.id}`, {
        name: m.name,
        duration_minutes: Number(m.duration_minutes),
        monthly_price: Number(m.monthly_price),
        active: m.active,
      });
      toast.success(`Modalidade "${m.name}" atualizada.`);
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving("");
    }
  };

  const saveSettings = async (e) => {
    e.preventDefault();
    setSaving("settings");
    try {
      await api.put("/settings", {
        min_age_default: Number(minAge),
        enrollment_fee: settings.enrollment_fee ?? 0,
        payment_methods: paymentMethods.split("\n").map((x) => x.trim()).filter(Boolean),
        lesson_generation_weeks: Number(genWeeks),
        lead_origins: leadOrigins.split("\n").map((x) => x.trim()).filter(Boolean),
        payment_due_day: Number(dueDay),
        prorate_rule: prorate,
        site_url: siteUrl.trim(),
      });
      toast.success("Configurações salvas.");
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving("");
    }
  };

  const updateModality = (id, patch) =>
    setModalities((list) => list.map((m) => (m.id === id ? { ...m, ...patch } : m)));

  return (
    <div className="space-y-8" data-testid="settings-page">
      <div>
        <h1 className="font-heading text-3xl font-bold">Configurações</h1>
        <p className="mt-1 text-sm text-muted-foreground">Valores, modalidades e regras gerais do IMJ.</p>
      </div>

      <section className="space-y-4">
        <h2 className="font-heading text-lg font-semibold">Modalidades e valores mensais</h2>
        <p className="text-sm text-muted-foreground">
          Valor mensal por modalidade — uma aula individual por semana, independente de o mês ter 4 ou 5 semanas. Sem taxa de matrícula.
        </p>
        <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
          {modalities.map((m) => (
            <div key={m.id} className="border border-border bg-white p-5" data-testid={`modality-card-${m.id}`}>
              <div className="space-y-3">
                <div className="space-y-1">
                  <Label className="text-xs uppercase tracking-wider text-muted-foreground">Nome</Label>
                  <Input value={m.name} onChange={(e) => updateModality(m.id, { name: e.target.value })} data-testid={`modality-name-${m.id}`} />
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-1">
                    <Label className="text-xs uppercase tracking-wider text-muted-foreground">Duração (min)</Label>
                    <Input type="number" min="10" max="180" value={m.duration_minutes} onChange={(e) => updateModality(m.id, { duration_minutes: e.target.value })} data-testid={`modality-duration-${m.id}`} />
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs uppercase tracking-wider text-muted-foreground">Valor mensal (R$)</Label>
                    <Input type="number" min="0" step="0.01" value={m.monthly_price} onChange={(e) => updateModality(m.id, { monthly_price: e.target.value })} data-testid={`modality-price-${m.id}`} />
                  </div>
                </div>
                <div className="flex items-center justify-between">
                  <Badge variant="secondary">1 aula individual/semana</Badge>
                  <Button
                    size="sm"
                    onClick={() => saveModality(m)}
                    disabled={saving === m.id}
                    className="bg-primary text-primary-foreground transition-colors hover:bg-primary/90"
                    data-testid={`modality-save-${m.id}`}
                  >
                    {saving === m.id ? "Salvando..." : "Salvar"}
                  </Button>
                </div>
              </div>
            </div>
          ))}
        </div>
      </section>

      <section className="max-w-xl space-y-4">
        <h2 className="font-heading text-lg font-semibold">Regras gerais</h2>
        {!settings ? (
          <p className="text-sm text-muted-foreground">Carregando...</p>
        ) : (
        <form onSubmit={saveSettings} className="space-y-4 border border-border bg-white p-5">
          <div className="space-y-1">
            <Label className="text-xs uppercase tracking-wider text-muted-foreground">Idade mínima padrão dos cursos</Label>
            <Input type="number" min="0" max="100" value={minAge} onChange={(e) => setMinAge(e.target.value)} data-testid="settings-min-age-input" />
            <p className="text-xs text-muted-foreground">Interessados abaixo desta idade serão encaminhados para análise da escola (módulo futuro).</p>
          </div>
          <div className="space-y-1">
            <Label className="text-xs uppercase tracking-wider text-muted-foreground">Geração de aulas (semanas à frente)</Label>
            <Input type="number" min="2" max="16" value={genWeeks} onChange={(e) => setGenWeeks(e.target.value)} data-testid="settings-gen-weeks-input" />
            <p className="text-xs text-muted-foreground">Período de aulas futuras geradas automaticamente a partir de cada matrícula ativa.</p>
          </div>
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <div className="space-y-1">
              <Label className="text-xs uppercase tracking-wider text-muted-foreground">Dia de vencimento das mensalidades</Label>
              <Input type="number" min="1" max="28" value={dueDay} onChange={(e) => setDueDay(e.target.value)} data-testid="settings-due-day-input" />
              <p className="text-xs text-muted-foreground">Dia do mês em que as mensalidades geradas vencem (1 a 28).</p>
            </div>
            <div className="space-y-1">
              <Label className="text-xs uppercase tracking-wider text-muted-foreground">Proporcionalidade do 1º mês</Label>
              <Select value={prorate} onValueChange={setProrate}>
                <SelectTrigger data-testid="settings-prorate-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="half">Até o dia 15: integral · após: 50%</SelectItem>
                  <SelectItem value="full">Sempre cobrar o mês integral</SelectItem>
                </SelectContent>
              </Select>
              <p className="text-xs text-muted-foreground">Regra aplicada à mensalidade do mês de início da matrícula.</p>
            </div>
          </div>
          <div className="space-y-1">
            <Label className="text-xs uppercase tracking-wider text-muted-foreground">Formas de pagamento (uma por linha)</Label>
            <Textarea rows={3} value={paymentMethods} onChange={(e) => setPaymentMethods(e.target.value)} data-testid="settings-payment-methods-input" />
          </div>
          <div className="space-y-1">
            <Label className="text-xs uppercase tracking-wider text-muted-foreground">Origens de interessados (uma por linha)</Label>
            <Textarea rows={4} value={leadOrigins} onChange={(e) => setLeadOrigins(e.target.value)} data-testid="settings-lead-origins-input" />
            <p className="text-xs text-muted-foreground">Opções de origem do cadastro de interessados (Site, WhatsApp, Instagram...).</p>
          </div>
          <div className="space-y-1">
            <Label className="text-xs uppercase tracking-wider text-muted-foreground">Endereço do site oficial</Label>
            <Input type="url" value={siteUrl} onChange={(e) => setSiteUrl(e.target.value)} data-testid="settings-site-url-input" />
            <p className="text-xs text-muted-foreground">Base dos links enviados nas respostas do WhatsApp (cursos, valores, matrícula etc.). Campo global — na futura versão multi-escola será um valor por escola.</p>
          </div>
          <div className="flex items-center justify-between">
            <p className="text-sm text-muted-foreground">Taxa de matrícula: <strong>R$ 0,00</strong></p>
            <Button type="submit" disabled={saving === "settings"} className="bg-primary text-primary-foreground transition-colors hover:bg-primary/90" data-testid="settings-save-button">
              {saving === "settings" ? "Salvando..." : "Salvar regras"}
            </Button>
          </div>
        </form>
        )}
      </section>
    </div>
  );
}
